import { Injectable, TemplateRef, inject, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Header Actions slot — permite que cada página inyecte botones contextuales
 * en el header (ej. "Nuevo cliente" en `/clientes`, "Exportar" en `/pedidos`).
 *
 * Por qué un service en vez de `<ng-content>`:
 *   - El header vive en `side-nav-outer-toolbar` (layout), las paginas son
 *     children del router-outlet 2-3 niveles abajo. Pasar contenido al header
 *     via `<ng-content>` requiere portales o un wrapper en cada page.
 *   - Un service con signal de TemplateRef es Angular-idiomatic, sin acoplar
 *     las páginas al layout.
 *
 * Uso desde una pagina:
 *
 *   @ViewChild('headerActions') headerActionsTpl!: TemplateRef<unknown>;
 *
 *   constructor(private headerActions: HeaderActionsService) {}
 *
 *   ngAfterViewInit() {
 *     this.headerActions.set(this.headerActionsTpl);
 *   }
 *
 *   ngOnDestroy() {
 *     // Auto-clear ya pasa en NavigationStart, esto es defensa extra.
 *     this.headerActions.clear();
 *   }
 *
 *   <!-- En el template -->
 *   <ng-template #headerActions>
 *     <button type="button" class="header-action-btn" (click)="nuevo()">
 *       <app-icon name="plus" [size]="14"></app-icon>
 *       Nuevo cliente
 *     </button>
 *   </ng-template>
 *
 * El service limpia automaticamente el slot en cada NavigationStart — asi la
 * pagina anterior no deja sus botones colgando cuando el usuario navega.
 */
@Injectable({ providedIn: 'root' })
export class HeaderActionsService {
  private router = inject(Router);

  /** TemplateRef del slot actual. null cuando no hay actions registradas. */
  readonly template = signal<TemplateRef<unknown> | null>(null);

  constructor() {
    // Limpiar el slot al iniciar una navegacion — la pagina nueva tendra que
    // re-registrar su template en ngAfterViewInit si quiere mostrar actions.
    // Usamos NavigationStart (no End) para evitar el "flash" del header con
    // los botones de la pagina anterior durante la transicion.
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe(() => this.template.set(null));
  }

  /** Registra el template del slot. La pagina llama esto en ngAfterViewInit. */
  set(tpl: TemplateRef<unknown> | null): void {
    this.template.set(tpl);
  }

  /** Limpia explicitamente. Util en ngOnDestroy o cuando una pagina quita actions
   *  dinamicamente (ej. cuando entra a modo "selection" y oculta los botones). */
  clear(): void {
    this.template.set(null);
  }
}
